"use client";

import { useState, useEffect } from "react";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import { useStickyCtaState } from "@/hooks/use-sticky-cta";
import { FaWhatsapp } from "react-icons/fa";
import { cn } from "@/lib/utils";

export default function WhatsAppButton() {
  const { trackWhatsappClicked } = useDataFastGoals();
  const phoneNumber = "5519993376029"; // Código do país (55) + número
  const whatsappUrl = `https://wa.me/${phoneNumber}`;
  const [showMessage, setShowMessage] = useState(false);
  const stickyCta = useStickyCtaState();

  // O balão entra depois de 5s e sai sozinho depois de 6s na tela. No mobile
  // ele ficava sobre a coluna de texto e comia o fim dos títulos; como nudge
  // já cumpriu o papel nos primeiros segundos. O botão continua fixo.
  useEffect(() => {
    const show = setTimeout(() => setShowMessage(true), 5000);
    const hide = setTimeout(() => setShowMessage(false), 11000);
    return () => {
      clearTimeout(show);
      clearTimeout(hide);
    };
  }, []);

  // No mobile o botão acompanha a barra fixa de CTA: enquanto o hero está na
  // tela a barra está recolhida e o botão some junto; quando a barra sobe, o
  // botão sobe com ela para não cobri-la. Em páginas sem hero (blog) a barra
  // não existe e o botão fica sempre visível. No desktop nada disso se aplica.
  const raised = stickyCta === "visible";
  const hiddenOnMobile = stickyCta === "hidden";

  // Cor: era o verde do WhatsApp em hex, com um ponto vermelho pulsando em
  // cima — notificação falsa, que prometia uma mensagem que não existia.
  // Agora é o azul da marca com o ícone do WhatsApp, que já diz o destino.
  return (
    // Escondido, é `invisible` só no celular: sai da ordem do Tab e da árvore
    // de acessibilidade lá, sem afetar o desktop (o estado da barra não sabe
    // o tamanho da tela). `visibility` entra na transição para trocar só no
    // fim do fade.
    <div
      className={cn(
        "fixed right-4 z-50 flex origin-bottom-right flex-col items-end gap-2 transition-[bottom,opacity,scale,visibility] duration-300 ease-(--lp-ease-out) md:right-6 md:bottom-6",
        raised ? "bottom-24" : "bottom-6",
        hiddenOnMobile && "max-md:invisible max-md:scale-90 max-md:opacity-0",
      )}
    >
      {/* Balão: decorativo, repete o rótulo do botão. */}
      <div
        aria-hidden="true"
        className={cn(
          "relative origin-bottom-right rounded-xl bg-popover px-4 py-2 text-sm font-medium text-popover-foreground shadow-lg ring-1 ring-border transition-[opacity,scale,translate] duration-300 ease-(--lp-ease-out)",
          showMessage
            ? "translate-y-0 scale-100 opacity-100"
            : "pointer-events-none translate-y-2 scale-95 opacity-0",
        )}
      >
        Precisa de ajuda?
      </div>

      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsappClicked("floating_button")}
        className="lp-press grid size-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        aria-label="Falar com a equipe no WhatsApp (abre em outra aba)"
      >
        <FaWhatsapp className="size-6" aria-hidden="true" />
      </a>
    </div>
  );
}
